import { PO_STATUS, SHIPPING_SCHEDULE_STATUS } from '../constants/statusValues';
import { CHILD_PO_CARRIER_OPTION_PARTNER } from '../constants/childPOMarketplaceRepost';

type PoLike = Record<string, unknown> | null | undefined;

function nestedPartyId(po: PoLike, field: string): string | null {
  const nested = po?.[field];
  if (nested && typeof nested === 'object' && 'id' in nested) {
    const id = (nested as { id?: unknown }).id;
    return typeof id === 'string' ? id : null;
  }
  return null;
}

/** True when the PO doc is assigned or pending for this carrier user. */
export function poDocAssignedToCarrier(po: PoLike, carrierUid: string): boolean {
  if (!po || !carrierUid) return false;
  if (po.carrierId === carrierUid) return true;
  if (nestedPartyId(po, 'selectedCarrier') === carrierUid) return true;
  if (nestedPartyId(po, 'pendingCarrier') === carrierUid) return true;
  if (nestedPartyId(po, 'approvedCarrier') === carrierUid) return true;
  if (nestedPartyId(po, 'approvedCarrierPendingRateConfirmation') === carrierUid) {
    return true;
  }
  return false;
}

function isInactivePoStatus(status: unknown): boolean {
  const normalized = String(status || '').toLowerCase();
  return (
    normalized === PO_STATUS.CANCELLED.toLowerCase() ||
    normalized === 'deleted' ||
    normalized === PO_STATUS.COMPLETED.toLowerCase() ||
    normalized === SHIPPING_SCHEDULE_STATUS.CANCELLED.toLowerCase() ||
    normalized === SHIPPING_SCHEDULE_STATUS.COMPLETED.toLowerCase()
  );
}

/** Schedule already assigned / running — partner request is no longer an Available Loads opportunity. */
function isAssignedShippingSchedule(status: unknown): boolean {
  const normalized = String(status || '').toLowerCase();
  return (
    normalized === SHIPPING_SCHEDULE_STATUS.ACTIVE.toLowerCase() ||
    normalized === SHIPPING_SCHEDULE_STATUS.IN_PROGRESS.toLowerCase()
  );
}

export function isChildPoPartnerAssignment(po: PoLike): boolean {
  if (!po) return false;
  if (po.carrierOption === CHILD_PO_CARRIER_OPTION_PARTNER) return true;
  return po.isMarketplace === false && !!po.carrierId;
}

/**
 * Whether a carrier partner request for a multi-load child should be hidden based on parent PO state.
 */
export function shouldBlockPartnerRequestForChildParent(
  childPo: PoLike,
  parentRecord: PoLike,
  carrierUid: string
): boolean {
  if (!childPo?.parentPONumber) return false;

  if (!parentRecord) {
    return !poDocAssignedToCarrier(childPo, carrierUid);
  }

  return isInactivePoStatus(parentRecord.status);
}

export type CarrierPartnerRequestGateResult =
  | { action: 'show' }
  | { action: 'hide' }
  | { action: 'close_stale'; reason: string };

/**
 * Production gate for carrier Partner Requests UI.
 * Returns close_stale when the request should be cancelled in Firestore (orphan/zombie).
 */
export function evaluateCarrierPartnerRequestGate(input: {
  partnerRequest: { id?: string; carrierId?: string; poNumber?: string };
  po: PoLike;
  parentPo?: PoLike;
  carrierUid: string;
}): CarrierPartnerRequestGateResult {
  const { partnerRequest, po, parentPo, carrierUid } = input;

  if (!po) {
    return { action: 'close_stale', reason: 'purchase_order_not_found' };
  }

  if (partnerRequest.carrierId && partnerRequest.carrierId !== carrierUid) {
    return { action: 'hide' };
  }

  if (isInactivePoStatus(po.status) || isInactivePoStatus(po.shippingScheduleStatus)) {
    return { action: 'close_stale', reason: 'purchase_order_inactive' };
  }

  // Active/In Progress schedule means assignment already committed (or load is running).
  // Close sticky `offered`/`pending` rows left behind when counter-accept/RC omitted PR updates.
  if (isAssignedShippingSchedule(po.shippingScheduleStatus)) {
    return { action: 'close_stale', reason: 'purchase_order_already_assigned' };
  }

  if (!isChildPoPartnerAssignment(po) && !poDocAssignedToCarrier(po, carrierUid)) {
    return { action: 'close_stale', reason: 'not_partner_assignment' };
  }

  if (!poDocAssignedToCarrier(po, carrierUid)) {
    return { action: 'close_stale', reason: 'carrier_not_assigned_on_po' };
  }

  if (po.isMultiLoad && po.parentPONumber) {
    if (shouldBlockPartnerRequestForChildParent(po, parentPo ?? null, carrierUid)) {
      if (!parentPo) {
        return { action: 'close_stale', reason: 'parent_po_missing' };
      }
      return { action: 'close_stale', reason: 'parent_po_inactive' };
    }
  }

  return { action: 'show' };
}

/** Shipper shipping schedule carrier column — broker schedule parity. */
export function resolveShipperScheduleCarrierLabel(po: PoLike): string {
  if (!po) return 'TBD';

  const isMarketplace =
    po.carrierOption === 'marketplace' ||
    (po.isMarketplace === true && !po.carrierId);

  if (isMarketplace) return 'Marketplace';

  const carrierRef = po.approvedCarrier || po.selectedCarrier || po.pendingCarrier;
  if (carrierRef) {
    if (typeof carrierRef === 'object' && carrierRef !== null) {
      const name = (carrierRef as { companyName?: string; id?: string }).companyName;
      const id = (carrierRef as { id?: string }).id;
      return name || id || 'Partnered Carrier';
    }
    return String(carrierRef);
  }

  if (po.carrierOption === CHILD_PO_CARRIER_OPTION_PARTNER || (po.isMarketplace === false && po.carrierId)) {
    return 'Partnered Carrier';
  }

  return 'TBD';
}

/** Shipper shipping schedule type column — broker schedule parity. */
export function resolveShipperScheduleTypeLabel(po: PoLike): string {
  if (!po) return 'Full Load';
  if (po.carrierOption === CHILD_PO_CARRIER_OPTION_PARTNER) return 'Partnered Carrier';
  if (po.carrierOption === 'marketplace' || po.isMarketplace === true) return 'Marketplace';
  return (po.type as string) || 'Full Load';
}
